import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import type { PassportEligibleCountry } from '@/hooks/usePassport';

interface CountryComboboxProps {
  id?: string;
  countries: PassportEligibleCountry[];
  value: string;
  onChange: (countryId: string) => void;
  disabled?: boolean;
  loading?: boolean;
}

// Plain case-insensitive substring matching rather than cmdk's default fuzzy
// scoring -- this is what makes "indo" -> Indonesia, "port" -> Portugal, etc.
// deterministic and easy to reason about, matching what a user actually expects
// from a country search rather than a fuzzy command-palette-style match.
const filterCountry = (countryName: string, search: string) =>
  countryName.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;

const CountryCombobox = ({ id, countries, value, onChange, disabled, loading }: CountryComboboxProps) => {
  const [open, setOpen] = useState(false);
  const selected = countries.find((country) => country.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || loading}
          className="w-full justify-between font-normal"
        >
          <span className={cn('truncate', !selected && 'text-muted-foreground')}>
            {loading ? 'Loading countries…' : selected ? selected.name : 'Select a country'}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] p-0"
      >
        <Command filter={filterCountry}>
          <CommandInput placeholder="Search countries…" />
          <CommandList>
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {countries.map((country) => (
                <CommandItem
                  key={country.id}
                  value={country.name}
                  onSelect={() => {
                    onChange(country.id);
                    setOpen(false);
                  }}
                >
                  <Check className={cn('mr-2 h-4 w-4', country.id === value ? 'opacity-100' : 'opacity-0')} />
                  {country.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default CountryCombobox;
